import { FormAction } from '@anytag/shared-types';
import { cn, getSelectOptions } from '@anytag/shared-utils';
import { UNEXPECTED_ERROR } from '@anytag/shared-constants';
import { useQueryHelper, useToggle } from '@anytag/shared-hooks';
import { SelectorField, TextField } from '@anytag/shared-molecules';
import { namedOperations, useCreateTiktokAdAccountMutation } from '@anytag/graphql';
import { BaseModal, Button, Form, ModalBody, ModalFooter } from '@anytag/shared-atoms';
import { INDUSTRY_OPTIONS, LOCATION_LIST_OPTIONS, TIMEZONE_OPTIONS } from '../TikTokDataOptions';
import { CreateAdAccountSchema } from './schema';
import { CreateAdAccountFormKeys, CreateAdAccountFormValues, CreateAdAccountProps } from './types';

export const CreateAdAccount = ({ className, bcList, hasAccountsLimitWarning }: CreateAdAccountProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { on, setOn, setOff } = useToggle();
  const { callCreateTiktokAdAccount, loading } = useCreateTiktokAdAccountMutation({
    refetchQueries: [namedOperations.Query.TiktokBusinessAdAccounts]
  });

  const translatedIndustriesOptions = INDUSTRY_OPTIONS.map((item) => ({
    ...item,
    label: t(item.label),
    submenu: item.submenu?.map((subItem) => ({ ...subItem, label: t(subItem.label) }))
  }));
  const translatedLocationOptions = LOCATION_LIST_OPTIONS.map((item) => ({
    ...item,
    label: t(`Country.${item.label}`)
  }));

  const handleSubmit: FormAction<CreateAdAccountFormValues>['onSubmit'] = async (
    { businessCenterId, industryId, adAccountName, adAccountCompanyName, timezone, locationCode, promotionLink },
    methods
  ) => {
    try {
      await callCreateTiktokAdAccount({
        variables: {
          input: {
            bcId: businessCenterId,
            industryId: Number(industryId),
            company: adAccountCompanyName,
            name: adAccountName,
            promotionLink,
            timezone,
            registeredArea: locationCode
          }
        }
      });

      enqueueSnackbar(t('succeededInCreating', { name: adAccountName }), { variant: 'success' });
      setOff();
    } catch (error) {
      enqueueSnackbar(t(error.message || UNEXPECTED_ERROR), { variant: 'error' });
    }
  };

  return (
    <>
      <Button
        variant="outline"
        title={t('Button.Create New')}
        onClick={setOn}
        className={cn(className, 'w-max px-6')}
        disabled={hasAccountsLimitWarning}
      />
      <BaseModal open={on} onClose={setOff} title={t('Dialog.Create TikTok Ad Account')} asChild>
        <Form.Provider<CreateAdAccountFormValues>
          onSubmit={handleSubmit}
          zodSchema={CreateAdAccountSchema}
          defaultValues={{
            industryId: '',
            businessCenterId: '',
            adAccountCompanyName: '',
            adAccountName: '',
            promotionLink: '',
            timezone: '',
            locationCode: 'TH'
          }}
        >
          {({ formState }) => (
            <>
              <ModalBody>
                <SelectorField<CreateAdAccountFormKeys>
                  name="businessCenterId"
                  title={t('Selector.Business Center')}
                  options={getSelectOptions(bcList)}
                  required
                  placeholder={t<string>('Select Business Center')}
                />
                <TextField<CreateAdAccountFormKeys>
                  name="adAccountName"
                  title={t('Ad Account Name')}
                  placeholder="Business Center name"
                  required
                />
                <TextField<CreateAdAccountFormKeys>
                  name="adAccountCompanyName"
                  title={t('Company Name')}
                  placeholder="AnyMind"
                  required
                />
                <TextField<CreateAdAccountFormKeys>
                  name="promotionLink"
                  title={t('Promoted Item URL')}
                  placeholder="https://anymindgroup.com"
                  required
                />
                <SelectorField<CreateAdAccountFormKeys>
                  name="timezone"
                  title={t('Selector.Timezone')}
                  options={TIMEZONE_OPTIONS}
                  required
                />
                <SelectorField<CreateAdAccountFormKeys>
                  name="industryId"
                  title={t('Selector.Industry of your business')}
                  options={translatedIndustriesOptions}
                  required
                />
                <SelectorField<CreateAdAccountFormKeys>
                  name="locationCode"
                  title={t('Selector.Location')}
                  options={translatedLocationOptions}
                  disabled
                />
              </ModalBody>

              <ModalFooter
                cancelButton={{ onClick: setOff }}
                submitButton={{ type: 'submit', title: t('Button.Create'), loading: formState.isSubmitting || loading }}
              />
            </>
          )}
        </Form.Provider>
      </BaseModal>
    </>
  );
};
